import PageLayout from "@/components/feature/PageLayout";
import ScrollReveal from "@/components/base/ScrollReveal";

const values = [
  {
    num: "01",
    title: "Clarity over comfort",
    body: "We help you understand what's actually driving your thoughts, feelings, and behaviors. Not just how to cope with symptoms, but why they're happening in the first place.",
    color: "#B4A6E3",
  },
  {
    num: "02",
    title: "The right therapist fit",
    body: "Not all therapists are the same. We take matching seriously, because the relationship between you and your therapist is the single most important factor in whether therapy works.",
    color: "#7D54FC",
  },
  {
    num: "03",
    title: "Structure that moves you forward",
    body: "Every session has a purpose. Therapy here isn't open-ended. It's intentional, goal-oriented, and built around real, measurable progress.",
    color: "#3C6FF8",
  },
  {
    num: "04",
    title: "A modern client experience",
    body: "Easy scheduling, clear communication, responsive support. We try to make the experience as low-friction as possible so therapy doesn't become another source of stress.",
    color: "#6B742F",
  },
];

export default function About() {
  return (
    <>
      <PageLayout>
        {/* Hero */}
        <section className="relative w-full min-h-[520px] md:min-h-[620px] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="/images/about/about-hero.png"
              alt="Perspective Therapy office interior"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(28,20,12,0.48) 0%, rgba(42,30,18,0.32) 50%, rgba(60,40,20,0.14) 100%)" }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(20,14,8,0.38) 0%, transparent 50%)" }} />
          </div>
          <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 pt-36 pb-20">
            <div className="max-w-2xl">
              <p className="font-manrope text-white/90 text-[13px] font-semibold tracking-widest uppercase mb-4">
                About Perspective Therapy
              </p>
              <h1 className="font-fraunces text-white text-4xl md:text-5xl lg:text-[3.4rem] font-light leading-[1.15] mb-6">
                Therapy built on real understanding,{" "}
                <em className="not-italic font-normal" style={{ color: "#C9BCEE" }}>
                  not just coping skills
                </em>
              </h1>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 md:p-4 max-w-lg">
                <p className="font-manrope text-[#2B2B2B]/90 text-lg leading-relaxed">
                  We're a mental health practice offering in-person care in Larchmont and telehealth in New York, New Jersey, and Indiana. We support children, teens, adults, couples, and groups.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Mission */}
        <section className="w-full py-20 md:py-28">
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <ScrollReveal variant="fadeLeft" delay={0}>
                <div className="w-full h-[280px] sm:h-[340px] md:h-[480px] rounded-2xl overflow-hidden">
                  <img
                    src="/images/about/mission.png"
                    alt="Perspective Therapy mission"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </ScrollReveal>
              <div>
                <ScrollReveal variant="fadeIn" delay={0}>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-5 h-[2px] rounded-full" style={{ background: "linear-gradient(90deg, #6B742F, #7D54FC)" }} />
                    <span className="font-manrope text-[13px] font-semibold tracking-widest uppercase" style={{ color: "#6B742F" }}>
                      Our Mission
                    </span>
                  </div>
                </ScrollReveal>
                <ScrollReveal variant="fadeUp" delay={80}>
                  <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-6">
                    Helping people make sense of what's going on. {" "}
                    <em className="not-italic" style={{ color: "#7D54FC" }}>and actually change it</em>
                  </h2>
                </ScrollReveal>
                <ScrollReveal variant="fadeUp" delay={160}>
                  <p className="font-manrope text-[#2B2B2B]/82 text-[15px] leading-relaxed mb-5">
                    Perspective Therapy was founded with a clear purpose: to offer therapy that goes deeper than symptom management. We believe that understanding the root of what you're dealing with is the first step toward real, lasting change.
                  </p>
                </ScrollReveal>
                <ScrollReveal variant="fadeUp" delay={240}>
                  <p className="font-manrope text-[#2B2B2B]/82 text-[15px] leading-relaxed mb-5">
                    Our therapists combine evidence-based methods with genuine human connection. We don't do vague or generic approaches. Each client gets a tailored plan that makes sense for their situation, their pace, and their goals.
                  </p>
                </ScrollReveal>
                <ScrollReveal variant="fadeUp" delay={320}>
                  <p className="font-manrope text-[#2B2B2B]/82 text-[15px] leading-relaxed">
                    Whether care is for a child, teen, adult, couple, or group participant, we meet clients where they are and help them move forward.
                  </p>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>

        {/* Our Values */}
        <section className="w-full py-20 md:py-28" style={{ background: "linear-gradient(180deg, #F8F5EE 0%, #F5F1E8 45%, #F2EDE4 100%)" }}>
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="max-w-3xl mb-14">
              <ScrollReveal variant="fadeIn" delay={0}>
                <span className="font-manrope text-[13px] font-semibold tracking-widest uppercase block mb-4" style={{ color: "#6B742F" }}>
                  What We Stand For
                </span>
              </ScrollReveal>
              <ScrollReveal variant="fadeUp" delay={80}>
                <h2 className="font-fraunces text-[#2B2B2B] text-4xl md:text-5xl font-light leading-snug">
                  Our values are the reason clients stay and{" "}
                  <em className="not-italic" style={{ color: "#7D54FC" }}>actually see results</em>
                </h2>
              </ScrollReveal>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
              {values.map((v, i) => (
                <ScrollReveal key={v.num} variant="fadeUp" delay={i * 90} threshold={0.08}>
                  <div
                    className="rounded-2xl p-7 border bg-white/80 card-hover"
                    style={{ borderColor: v.color + "22" }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLDivElement).style.borderColor = v.color + "45";
                      (e.currentTarget as HTMLDivElement).style.background = "rgba(255,255,255,1)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLDivElement).style.borderColor = v.color + "22";
                      (e.currentTarget as HTMLDivElement).style.background = "rgba(255,255,255,0.80)";
                    }}
                  >
                    <div className="h-[3px] w-10 rounded-full mb-5" style={{ background: v.color }} />
                    <div className="flex items-start gap-4">
                      <span className="font-manrope text-sm font-bold shrink-0 mt-0.5" style={{ color: v.color }}>{v.num}</span>
                      <div>
                        <h3 className="font-fraunces text-[#2B2B2B] text-lg font-medium mb-2">{v.title}</h3>
                        <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed">{v.body}</p>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Who We Are */}
        <section className="w-full py-20 md:py-28">
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div>
                <ScrollReveal variant="fadeIn" delay={0}>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-5 h-[2px] rounded-full" style={{ background: "linear-gradient(90deg, #3C6FF8, #7D54FC)" }} />
                    <span className="font-manrope text-[13px] font-semibold tracking-widest uppercase" style={{ color: "#3C6FF8" }}>
                      The Practice
                    </span>
                  </div>
                </ScrollReveal>
                <ScrollReveal variant="fadeUp" delay={80}>
                  <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-6">
                    Serving New York, New Jersey, and Indiana with{" "}
                    <em className="not-italic" style={{ color: "#7D54FC" }}>telehealth and in-person care</em>
                  </h2>
                </ScrollReveal>
                <ScrollReveal variant="fadeUp" delay={160}>
                  <p className="font-manrope text-[#2B2B2B]/82 text-[15px] leading-relaxed mb-5">
                    Perspective Therapy offers telehealth sessions in New York, New Jersey, and Indiana and in-person appointments at our Larchmont, New York office. Our team of licensed and pre-licensed therapists brings a range of specialties and approaches, so we can match you with someone who truly fits.
                  </p>
                </ScrollReveal>
                <ScrollReveal variant="fadeUp" delay={240}>
                  <p className="font-manrope text-[#2B2B2B]/82 text-[15px] leading-relaxed mb-8">
                    As a practice, we serve children, teens, adults, couples, and groups. Each therapist's populations, state licensure, service format, and current availability are listed on their profile. Our therapists use evidence-based approaches such as REBT, Brain Spotting, Psychodynamic Therapy, and trauma-informed care. Our therapists have a variety of training, which is essential to help us tailor to our clients' needs.
                  </p>
                </ScrollReveal>

              </div>
              <ScrollReveal variant="fadeRight" delay={0}>
                <div className="w-full h-[280px] sm:h-[340px] md:h-[480px] rounded-2xl overflow-hidden">
                  <img
                    src="/images/about/practice.png"
                    alt="Perspective Therapy practice"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="w-full py-20 md:py-28" style={{ background: "linear-gradient(180deg, #F2EEE4 0%, #EEE8DA 45%, #F5F1E8 100%)" }}>
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="max-w-2xl mx-auto text-center">
              <ScrollReveal variant="fadeUp" delay={0}>
                <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-5">
                  Ready to learn more about working with us?
                </h2>
              </ScrollReveal>
              <ScrollReveal variant="fadeUp" delay={80}>
                <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed mb-8 max-w-md mx-auto">
                  Browse our therapist profiles or use the chatbot for help identifying which therapist may be the best fit for your needs.
                </p>
              </ScrollReveal>
              <ScrollReveal variant="fadeUp" delay={160}>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="/therapists"
                    className="inline-flex items-center justify-center gap-2 text-white font-manrope font-semibold text-[15px] px-7 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                    style={{ background: "#3C6FF8" }}
                    onMouseEnter={(e) => (e.currentTarget as HTMLAnchorElement).style.background = "#2a5de6"}
                    onMouseLeave={(e) => (e.currentTarget as HTMLAnchorElement).style.background = "#3C6FF8"}
                  >
                    Find Your Therapist <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>
      </PageLayout>
    </>
  );
}
